import type { TabSurface, ToolFailure } from "../../bus/rules/types.js";
import type { StudioToolDefinition } from "../../viewer/rules/context.js";
import { entitlementWithheld } from "../../viewer/rules/viewer.js";
import { SEARCH_TAB_ID } from "../rules/tabs.js";

/**
 * MAX_OPEN_COPILOT_TABS caps the co-pilot tabs open at once. The strip holds one conversation:
 * `studio.tabs.create` answers `limit-reached` while one is open, and `studio.tabs.open`
 * focuses the one that is. An agent's create and a person's run the same gate.
 *
 * A host bounds its restorable `threads` search param against this same constant. That URL is
 * attacker-controllable and the provider mounts one live session, and one event stream, per
 * entry, so the two gates read one number.
 */
export const MAX_OPEN_COPILOT_TABS = 1;

/**
 * unknownTab answers the `not-found` refusal for an id the strip says it does not hold, or null
 * for an id it holds and for a strip that states no `hasTab`, which is not asked.
 */
const unknownTab = (tabs: TabSurface, tabId: string): ToolFailure | null =>
  tabs.hasTab?.(tabId) === false
    ? { code: "not-found", error: `no tab is open under ${tabId}`, ok: false }
    : null;

/**
 * switchTab activates the named tab. SEARCH_TAB_ID shows the search panel; any
 * other id activates a co-pilot conversation (the threads store rejects an unknown id).
 * This tool is the only switching surface the UI uses, so a clicking human
 * drives the same mutation path.
 *
 * It answers `not-found` for an id the strip says it does not hold, and switches nothing. The
 * search pseudo-tab is always there, so the strip is not asked about it. A strip that states no
 * `TabSurface.hasTab` is not asked at all: the tool then answers success for any id, since
 * `setActiveTab` reports nothing back. `closeTab` answers the same way for the same reason.
 *
 * agentHidden because a run is hosted BY a tab: switching away slides the
 * streaming conversation (and the Stop control that interrupts it) off the
 * panel, so the model would leave the user watching a turn they can neither
 * follow nor cancel. The surface that runs the agent is not a surface the agent
 * steers; studio.tabs.open stays visible as the reveal seam because it focuses
 * rather than displaces. The human tab strip is untouched: agentHidden filters
 * only the LLM manifest, never the gate.
 */
export const switchTab: StudioToolDefinition<"studio.tabs.switch"> = {
  agentHidden: true,
  handler: (ctx, input) => {
    if (!ctx.tabs) {
      return { code: "no-session", error: "tab surface not mounted", ok: false };
    }

    const unknown = input.tabId === SEARCH_TAB_ID ? null : unknownTab(ctx.tabs, input.tabId);
    if (unknown !== null) {
      return unknown;
    }

    ctx.tabs.setActiveTab(input.tabId);

    return { ok: true, summary: `Switched the active tab to ${input.tabId}.` };
  },
};

/**
 * createTab opens a new co-pilot tab and switches the panel to it. Two gates stand before it: a
 * viewer whose agreement does not cover the co-pilot (no `copilot` entitlement) is refused with
 * `upgrade-required`, and the surface that states the fact is `studio.tabs.open`'s; an entitled
 * viewer is capped at {@link MAX_OPEN_COPILOT_TABS} and refused with `limit-reached` once the strip
 * is full. The threads store returns the minted thread id so the summary can name it.
 */
export const createTab: StudioToolDefinition<"studio.tabs.create"> = {
  handler: (ctx) => {
    if (!ctx.viewer.hasEntitlement("copilot")) {
      return entitlementWithheld("copilot");
    }

    if (!ctx.tabs) {
      return { code: "no-session", error: "tab surface not mounted", ok: false };
    }

    if (ctx.tabs.countOpen() >= MAX_OPEN_COPILOT_TABS) {
      return { code: "limit-reached", error: "chat limit reached", ok: false };
    }

    const id = ctx.tabs.addCopilotTab();

    return { ok: true, summary: `Opened a new co-pilot tab (${id}).` };
  },
};

/**
 * openTab reveals the co-pilot and activates it: the one "Ask the co-pilot" entry point. Unlike
 * createTab it never stacks a second conversation and never fails on the open-tab cap:
 * `openCopilot` focuses the open conversation, opens one when none is, and for a
 * viewer without `copilot` reveals the pseudo-tab that states the agreement does not cover it,
 * so the entry always lands on the panel. That pseudo-tab is the gate, so this tool asks for no
 * entitlement.
 */
export const openTab: StudioToolDefinition<"studio.tabs.open"> = {
  handler: (ctx) => {
    if (!ctx.tabs) {
      return { code: "no-session", error: "tab surface not mounted", ok: false };
    }

    const id = ctx.tabs.openCopilot();

    return { ok: true, summary: `Revealed the co-pilot (${id}).` };
  },
};

/**
 * closeTab closes a co-pilot tab. The search pseudo-tab is sticky: a request to close it returns
 * a structured failure rather than mutating state, so a stray dispatch cannot remove the
 * always-on panel.
 *
 * It answers `not-found` for an id the strip says it does not hold, as `switchTab` does.
 *
 * agentHidden because closing a co-pilot tab tears down the conversation it hosts (the
 * session is closed and disposed), and the tab the model can name is the one running it, so
 * the call would end the very turn that made it. The surface that
 * runs the agent cannot be a tool the agent runs. The human close button is
 * untouched: agentHidden filters only the LLM manifest, never the gate.
 */
export const closeTab: StudioToolDefinition<"studio.tabs.close"> = {
  agentHidden: true,
  handler: (ctx, input) => {
    if (input.tabId === SEARCH_TAB_ID) {
      return { code: "invalid-input", error: "the search tab cannot be closed", ok: false };
    }

    if (!ctx.tabs) {
      return { code: "no-session", error: "tab surface not mounted", ok: false };
    }

    const unknown = unknownTab(ctx.tabs, input.tabId);
    if (unknown !== null) {
      return unknown;
    }

    ctx.tabs.closeCopilotTab(input.tabId);

    return { ok: true, summary: `Closed co-pilot tab ${input.tabId}.` };
  },
};
